/**
 * Azure Search Debug Utilities
 * 
 * This file contains utility functions to help debug and fix issues
 * with the Azure Search store when it gets stuck.
 * Search requests are now proxied through the backend API.
 */

import { azureSearchAuthService } from './AzureSearchAuthService';
import { nextGenService } from './NextGenService';

/**
 * Event name for direct suggestions updates
 */
export const AZURE_SEARCH_DIRECT_SUGGESTIONS_EVENT = 'azure-search-suggestions';
export const AZURE_SEARCH_FORCE_UPDATE_EVENT = 'azure-search-force-update';

/**
 * Azure Search utility functions for working with Azure Search stores
 */
export class AzureSearchUtil {
    /**
     * Reset a store that's stuck in "fetching" state
     * @param store The AzSearchStore instance to reset
     */
    public static resetStore(store: any): void {
        if (!store) {
            console.error('❌ Debug: Cannot reset null store');
            return;
        }

        console.log('🧹 Debug: Resetting store state');
        
        try {
            // Step 1: Clear any pending suggestions
            if (typeof store.clearSuggestions === 'function') {
                store.clearSuggestions();
                console.log('✅ Debug: Cleared suggestions');
            }
            
            // Step 2: Get the current state to analyze
            const state = store.getState();
            console.log('📊 Debug: Current state:', state);
            
            // Step 3: Force clear isFetching flag using Redux action if possible
            if (store.store && typeof store.store.dispatch === 'function') {
                console.log('🔄 Debug: Attempting to dispatch clear state action');
                
                store.store.dispatch({
                    type: 'RECEIVE_SUGGESTIONS',
                    suggestions: {
                        value: []
                    },
                    receivedAt: Date.now()
                });
                
                store.store.dispatch({
                    type: 'CLEAR_SUGGESTIONS'
                });
                
                console.log('✅ Debug: Dispatched reset actions');
            }

            // Step 4: Re-set the suggestCallback
            if (typeof store.setSuggestCallback === 'function') {
                console.log('🔄 Debug: Re-setting suggestCallback');
                store.setSuggestCallback(azureSearchAuthService.suggestCallback.bind(azureSearchAuthService));
            }
            
            // Step 5: Check if state was reset
            const newState = store.getState();
            console.log('📊 Debug: New state after reset:', newState);
            
            if (newState.suggestions && newState.suggestions.isFetching) {
                console.warn('⚠️ Debug: Store is still in fetching state after reset');
            } else {
                console.log('✅ Debug: Store successfully reset');
            }
        } catch (error) {
            console.error('❌ Debug: Error resetting store:', error);
        }
    }
    
    /**
     * Make a direct suggest API call through the backend proxy
     * @param store The store with configuration
     * @param searchTerm The search term to suggest
     */
    public static async suggestDirect(store: any, searchTerm: string): Promise<any> {
        try {
            if (!store) {
                throw new Error('Store is required');
            }
            
            const state = store.getState();
            if (!state.config || !state.config.index) {
                throw new Error('Store is not properly configured');
            }
            
            console.log('🧪 Debug: Making proxied suggest API call');
            
            const parameters = state.parameters?.suggestionsParameters || {};
            const suggesterName = parameters.suggesterName || 'default';
            const select = parameters.select || '';
            const top = parameters.top || 10;
            
            const postBody = {
                search: searchTerm,
                suggesterName: suggesterName,
                select: select,
                top: top,
                filter: parameters.filter || '',
                fuzzy: parameters.fuzzy || false
            };
            
            console.log('📦 Debug: Request parameters:', postBody);

            const proxyResponse = await nextGenService.proxySuggestRequest(state.config.index, postBody);
            
            console.log('📦 Debug: API response:', {
                status: proxyResponse.status,
                hasValue: !!proxyResponse.data?.value,
                valueCount: proxyResponse.data?.value?.length || 0,
                firstValue: proxyResponse.data?.value?.[0] || null
            });
            
            return proxyResponse.data;
        } catch (error) {
            console.error('❌ Debug: Error in suggestDirect:', error);
            throw error;
        }
    }
}

export default AzureSearchUtil;
